/**
 * transport/session.ts —— 入口侧会话客户端（**唯一出网入口**之一；`CONTRACT` §2／§6）。
 *
 * 纪律：
 *  · 路径经 `assertEgress` 白名单校验（同源相对路径；绝对 URL／协议相对 ⇒ 抛）；
 *  · **浏览器零凭据**：不带任何密钥/签名；cookie 由浏览器按同源自动携带（`credentials: "same-origin"`）；
 *  · 身份**不自报**：登录成功后的主体取自**服务端回执**（`actor`），前端不自行编造。
 */
import { assertEgress } from "./whitelist";
import { E_CODES } from "./errorText";

export interface SessionActor {
  actor: string;
}

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

export class SessionError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(E_CODES.AUTHN_FAILED + "：" + message);
    this.name = "SessionError";
    this.status = status;
  }
}

function doFetch(url: string, init: RequestInit, fetchFn: FetchLike): Promise<Response> {
  return fetchFn(url, { credentials: "same-origin", ...init });
}

/** 登录：凭据 → 由入口侧颁发会话 cookie（本函数**不接管**任何凭据存储） */
export async function login(
  user: string,
  password: string,
  fetchFn: FetchLike = fetch,
): Promise<SessionActor> {
  const path = assertEgress("login", "/api/auth/login");
  const res = await doFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user, password }),
  }, fetchFn);
  if (!res.ok) throw new SessionError(res.status, "登录被拒");
  const data = (await res.json()) as SessionActor;
  return data;
}

/** 探活：当前会话主体（未登录 ⇒ SessionError(401)） */
export async function currentActor(fetchFn: FetchLike = fetch): Promise<SessionActor> {
  const path = assertEgress("me", "/api/auth/me");
  const res = await doFetch(path, { method: "GET" }, fetchFn);
  if (!res.ok) throw new SessionError(res.status, "未登录");
  return (await res.json()) as SessionActor;
}

/** 登出：入口侧清除会话 cookie */
export async function logout(fetchFn: FetchLike = fetch): Promise<void> {
  const path = assertEgress("logout", "/api/auth/logout");
  const res = await doFetch(path, { method: "POST" }, fetchFn);
  if (!res.ok) throw new SessionError(res.status, "登出失败");
}
